import { ref, set, get, push, remove, update } from 'firebase/database';
import { db } from './config';

// Generic read with error handling
export const readData = async (path: string) => {
  try {
    const snapshot = await get(ref(db, path));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error(`Firebase read error (${path}):`, error);
    return null;
  }
};

// Generic write
export const writeData = async (path: string, data: unknown) => {
  await set(ref(db, path), data);
};

// Generic update
export const updateData = async (path: string, data: Record<string, unknown>) => {
  await update(ref(db, path), data);
};

// Generic push (auto-id)
export const pushData = async (path: string, data: unknown) => {
  const newRef = push(ref(db, path));
  await set(newRef, data);
  return newRef.key;
};

// Generic delete
export const deleteData = async (path: string) => {
  await remove(ref(db, path));
};

// PUBLIC write for messages (no auth required)
export const pushPublicMessage = async (data: unknown) => {
  try {
    const messagesRef = ref(db, 'messages');
    const newRef = push(messagesRef);
    await set(newRef, data);
    return newRef.key;
  } catch (error) {
    console.error('Firebase write error:', error);
    throw error;
  }
};

// Seed default portfolio data
export const seedDefaultData = async () => {
  const profile = await readData('profile');
  if (!profile) {
    await writeData('profile', {
      name: 'Mustakim Nur',
      role: 'Full-Stack Developer',
      bio: 'I build exceptional digital experiences that are fast, accessible, and visually stunning. Passionate about turning complex problems into elegant solutions.',
      email: 'mustakim@example.com',
      phone: '+880 1234-567890',
      location: 'Dhaka, Bangladesh',
      github: 'https://github.com/mustakim',
      linkedin: 'https://linkedin.com/in/mustakim',
      twitter: 'https://twitter.com/mustakim',
      profileImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      yearsExperience: '3+',
      projectsCompleted: '50+',
      clientsSatisfied: '30+',
    });

    await writeData('skills', {
      '-1': { name: 'React / Next.js', level: 92, category: 'Frontend' },
      '-2': { name: 'TypeScript', level: 88, category: 'Frontend' },
      '-3': { name: 'Node.js', level: 85, category: 'Backend' },
      '-4': { name: 'Firebase', level: 80, category: 'Backend' },
      '-5': { name: 'UI/UX Design', level: 75, category: 'Design' },
      '-6': { name: 'Tailwind CSS', level: 95, category: 'Frontend' },
    });

    await writeData('services', {
      '-1': { title: 'Web Development', description: 'Full-stack web applications with modern frameworks.', icon: '🌐' },
      '-2': { title: 'UI/UX Design', description: 'Beautiful, intuitive interfaces users love.', icon: '🎨' },
      '-3': { title: 'Mobile Apps', description: 'Cross-platform mobile apps with React Native.', icon: '📱' },
      '-4': { title: 'API Development', description: 'Scalable REST & GraphQL APIs.', icon: '⚡' },
      '-5': { title: 'Consulting', description: 'Technical consulting for digital growth.', icon: '💡' },
      '-6': { title: 'DevOps & CI/CD', description: 'Automated pipelines and cloud deployment.', icon: '🚀' },
    });

    await writeData('testimonials', {
      '-1': { name: 'Sarah Johnson', role: 'CEO, TechCorp', text: 'Mustakim delivered an exceptional product. Outstanding attention to detail.', avatar: 'SJ', rating: 5 },
      '-2': { name: 'Ahmed Rahman', role: 'Founder, StartupX', text: 'Working with Mustakim was a pleasure. He understood our vision perfectly.', avatar: 'AR', rating: 5 },
      '-3': { name: 'Emily Chen', role: 'Product Manager', text: 'The best developer I\'ve worked with. Professional and fast.', avatar: 'EC', rating: 5 },
    });

    await writeData('projects', {
      '-1': { title: 'E-Commerce Platform', description: 'Full-stack e-commerce with React, Node.js, and Stripe.', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop', tags: ['React', 'Node.js', 'MongoDB'], github: 'https://github.com', live: 'https://example.com', featured: true },
      '-2': { title: 'AI Chat Application', description: 'Real-time AI chat using OpenAI GPT-4 and Firebase.', image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&h=400&fit=crop', tags: ['Next.js', 'OpenAI', 'Firebase'], github: 'https://github.com', live: 'https://example.com', featured: true },
      '-3': { title: 'Dashboard Analytics', description: 'Analytics dashboard with real-time data visualization.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop', tags: ['React', 'D3.js', 'TypeScript'], github: 'https://github.com', live: 'https://example.com', featured: false },
    });

    await writeData('footer', {
      brandName: 'Mustakim.dev',
      brandTagline: 'Building exceptional digital experiences. Available for freelance & full-time opportunities.',
      copyrightText: 'Mustakim Nur Salin',
      availabilityStatus: 'Available for freelance',
      availabilitySubtext: 'Currently accepting new projects',
      responseTime: 'Within 24 hours',
      statusText1: 'Available for work',
      statusText2: 'Open to remote positions',
      githubUrl: 'https://github.com',
      linkedinUrl: 'https://linkedin.com',
      twitterUrl: 'https://twitter.com',
      emailUrl: 'mailto:mustakim@example.com',
    });

    await writeData('header', {
      brandName: 'Mustakim.dev',
      ctaText: 'Hire Me',
    });

    await writeData('contact', {
      heading: 'Contact Me',
      subheading: 'Get in touch',
      description: 'Have a project in mind? Let\'s build something amazing together.',
      talkTitle: 'Let\'s talk',
      talkDescription: 'I\'m always open to discussing new projects, creative ideas, or opportunities.',
      email: 'mustakim@example.com',
      phone: '+880 1234-567890',
      location: 'Dhaka, Bangladesh',
      availabilityTitle: 'Available for freelance',
      availabilitySubtext: 'Currently accepting new projects',
    });

    await writeData('theme', {
      neonCyan: '0 0% 80%',
      neonBlue: '220 10% 55%',
      neonGreen: '120 15% 50%',
      neonPurple: '260 10% 55%',
      neonPink: '340 10% 55%',
      background: '0 0% 3%',
      foreground: '0 0% 88%',
      primary: '0 0% 75%',
      randomOnLoad: false,
      autoSwitch: false,
    });

    await writeData('nfc', {
      name: '',
      role: '',
      bio: '',
      profileImage: '',
      statusBadge: 'Available for hire',
      showFullInfoBtn: true,
      fullInfoBtnText: 'View My Full Information',
      images: {},
      socials: {},
    });
  }
};
